import { getTestOverrides } from '@wix/cli-test-overrides';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { isHttpError, isHttpApplicationError } from '@wix/cli-http-client';
import type { HttpClient, HttpError } from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { queryLocales } from '@wix/ambassador-multilingual-locales-v2-locale/http';
import type {
  QueryLocalesRequest,
  QueryLocalesResponse,
} from '@wix/ambassador-multilingual-locales-v2-locale/types';
import {
  pushTranslations as pushTranslationsToMultilingual,
  pullTranslations,
} from '@wix/ambassador-headless-multilingual-v1-no-entity/http';
import type { TranslationKeysPayload } from '@wix/ambassador-headless-multilingual-v1-no-entity/types';
import { z } from 'zod';

const flatTranslationsSchema = z.record(z.string(), z.string());
const namespacedTranslationsSchema = z.record(
  z.string(),
  z.record(z.string(), z.string())
);

export type Translations =
  | z.infer<typeof flatTranslationsSchema>
  | z.infer<typeof namespacedTranslationsSchema>;

export class MultilingualClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout } = getTestOverrides();

    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  queryLocales = async (
    request?: QueryLocalesRequest
  ): Promise<QueryLocalesResponse> => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            queryLocales({
              ...request,
            })
          ),
        this.retryOptions
      );

      return data;
    } catch (error) {
      if (isHttpError(error) && isHttpApplicationError(error)) {
        throw new CliError({
          code: CliErrorCode.FailedToQueryLocales(),
          cause: error,
        });
      }

      throw error;
    }
  };

  getPrimaryLanguage = async (): Promise<string> => {
    const localesResponse = await this.queryLocales();
    const primaryLocale = localesResponse.locales?.find(
      (locale) => locale.primaryLocale === true
    );

    if (!primaryLocale?.languageCode) {
      throw new CliError({
        code: CliErrorCode.FailedToFetchPrimaryLanguage(),
        cause: new Error(
          'Failed to fetch the primary language. Please ensure Wix Multilingual is installed on your site.'
        ),
      });
    }

    return primaryLocale.languageCode;
  };

  pushTranslations = async (translations: Translations): Promise<void> => {
    const translationKeys = this.convertToTranslationKeysPayload(translations);

    try {
      await pRetry(
        () =>
          this.httpClient.request(
            pushTranslationsToMultilingual({ translationKeys })
          ),
        this.retryOptions
      );
    } catch (error) {
      if (isHttpError(error) && isHttpApplicationError(error)) {
        this.handleApplicationError(error);
      }

      throw new CliError({
        code: CliErrorCode.FailedToPushTranslations(),
        cause: error,
      });
    }
  };

  pullAllTranslations = async (): Promise<
    Record<string, Record<string, string>>
  > => {
    const allTranslations: Record<string, Record<string, string>> = {};
    let cursor: string | null | undefined;
    let hasMore = true;

    try {
      while (hasMore) {
        const { data } = await pRetry(
          () =>
            this.httpClient.request(
              pullTranslations({ paging: cursor ? { cursor } : undefined })
            ),
          this.retryOptions
        );

        for (const [locale, payload] of Object.entries(
          data.translationsByLanguage ?? {}
        )) {
          const flatKeys = this.flattenTranslationPayload(payload);

          if (!allTranslations[locale]) {
            allTranslations[locale] = {};
          }
          Object.assign(allTranslations[locale], flatKeys);
        }

        hasMore = data.pagingMetadata?.hasNext ?? false;
        cursor = data.pagingMetadata?.cursors?.next;
      }

      if (Object.keys(allTranslations).length === 0) {
        throw new CliError({
          code: CliErrorCode.NoTranslationPublishedContents(),
          cause: null,
        });
      }

      return allTranslations;
    } catch (error) {
      if (error instanceof CliError) {
        throw error;
      }

      if (isHttpError(error) && isHttpApplicationError(error)) {
        this.handleApplicationError(error);
      }

      throw new CliError({
        code: CliErrorCode.FailedToPullTranslations(),
        cause: error,
      });
    }
  };

  private convertToTranslationKeysPayload(
    translations: Translations
  ): TranslationKeysPayload {
    const flatResult = flatTranslationsSchema.safeParse(translations);

    if (flatResult.success) {
      return {
        translationKeys: { keys: flatResult.data },
      };
    }

    const namespacedResult = namespacedTranslationsSchema.parse(translations);
    const namespacedKeys: Record<string, { keys: Record<string, string> }> = {};
    for (const [ns, keys] of Object.entries(namespacedResult)) {
      namespacedKeys[ns] = { keys };
    }

    return { namespacedTranslationKeys: { namespacedKeys } };
  }

  private flattenTranslationPayload(
    payload: TranslationKeysPayload
  ): Record<string, string> {
    if (payload.translationKeys?.keys) {
      return payload.translationKeys.keys;
    }

    if (payload.namespacedTranslationKeys?.namespacedKeys) {
      const result: Record<string, string> = {};
      for (const [ns, translationKeys] of Object.entries(
        payload.namespacedTranslationKeys.namespacedKeys
      )) {
        for (const [key, value] of Object.entries(translationKeys.keys ?? {})) {
          result[`${ns}:${key}`] = value;
        }
      }
      return result;
    }

    return {};
  }

  private handleApplicationError(error: HttpError): never {
    const applicationError = error.response?.data.details.applicationError;

    if (
      applicationError?.code === 'APP_NOT_INSTALLED' &&
      error.response?.status === 428
    ) {
      throw new CliError({
        code: CliErrorCode.MultilingualAppNotInstalled(),
        cause: error,
      });
    }

    throw error;
  }
}
